import React from "react";
import { useSelector } from 'react-redux';
import {
    useMediaQuery,
    CircularProgress,
    Box,
    Typography,
    Button,
    Stack,
    Chip,
    Divider,
    Card,
    CardContent,
    LinearProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import AirplaneTicketIcon from "@mui/icons-material/AirplaneTicket";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import FlightLandIcon from "@mui/icons-material/FlightLand";
import AirlinesIcon from '@mui/icons-material/Airlines';
import FlightIcon from '@mui/icons-material/Flight';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { LAYOUT_HEIGHT, DIVERT_RETURN_STATUS } from 'state/constants';
import { formatTZDate } from 'state/helpers';
import FlexCenter from "components/FlexCenter";


const getStatusColor = (status = "") => {
    const normalized = status?.trim().toLowerCase();
    if (DIVERT_RETURN_STATUS.some(s => s.toLowerCase() === normalized)) return "warning";
    if (/not yet departed/i.test(status)) return "paper";
    if (/departed/i.test(status)) return "info";
    if (/in flight/i.test(status)) return "info";
    if (/arrived/i.test(status)) return "success";
    return "default";
};


const MobileFlightCard = ({ f, idx, prevTa, td, onSelect }) => {
    const theme = useTheme();
    const canSelect = f.flightStatus?.toLowerCase() === "not yet departed";
    // const preStatus = f.prevFlightStatus;
    const curStatus = f.flightStatus?.toLowerCase() !== "not yet departed" ? "Departed" : f.flightStatus;

    return (
        <Card
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "background.paper",
            }}
        >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                {/* Header: index + flightDesc + route */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.75}
                    mb={1}
                >
                    <Chip
                        label={idx + 1}
                        size="small"
                        color="info"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.65rem", minWidth: 28 }}
                    />
                    <Typography variant="subtitle2" fontWeight={600} noWrap>
                        {f.flightDesc}
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary" noWrap>
                        {f.dep || "—"} → {f.arr || "—"}
                    </Typography>
                    {/* <Typography variant="subtitle2" color="text.secondary" noWrap>
                        {f.prevEta || ""} - {f.etd || ""}
                    </Typography> */}
                </Stack>

                {/* Times row */}
                <Stack 
                    direction="row" 
                    spacing={2} 
                    mb={1} 
                    flexWrap="wrap" 
                    useFlexGap
                >
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={0.5}
                        sx={{ minWidth: 160 }}
                    >
                        <FlightLandIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" color="text.secondary">
                            {prevTa || ""}
                        </Typography>
                        {f.prevFlightStatus && (
                            <Chip
                                size="small"
                                variant="outlined"
                                color={getStatusColor(f.prevFlightStatus)}
                                label={f.prevFlightStatus}
                                sx={{ fontSize: "0.55rem", height: 16 }}
                            />
                        )}
                    </Stack>
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={0.5}
                        sx={{ minWidth: 160 }}
                    >
                        <FlightTakeoffIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" color="text.secondary">
                            {td || ""}
                        </Typography>
                        {curStatus && (
                            <Chip
                                size="small"
                                variant="outlined"
                                color={getStatusColor(curStatus)}
                                label={curStatus}
                                sx={{ fontSize: "0.55rem", height: 16 }}
                            />
                        )}
                    </Stack>
                </Stack>

                {/* Footer: aircraft + progress + button */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={0.5} 
                        sx={{ minWidth: 120, flexShrink: 1 }}
                    >
                        <AirplaneTicketIcon sx={{ fontSize: 14 }} color="action" />
                        <Typography variant="caption" noWrap color="text.secondary">
                            {f.acType || ""} · {f.flightReg || ""}
                        </Typography>
                    </Stack>

                    <Stack 
                        direction="row" 
                        alignItems="center" 
                        spacing={0.75} 
                        flex={1} 
                        minWidth={60}
                    >
                        <Box flex={1}>
                            <LinearProgress
                                variant="determinate"
                                value={typeof f.progress === "number" ? f.progress : 0}
                                color={f.progress === 100 ? "success" : "primary"}
                                sx={{ height: 5, borderRadius: 1 }}
                            />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {typeof f.progress === "number" ? `${f.progress}%` : "—"}
                        </Typography>
                    </Stack>

                    <Button
                        disabled={!canSelect}
                        size="small"
                        variant={canSelect ? "contained" : "outlined"}
                        color="primary"
                        startIcon={<AssignmentIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => { e.stopPropagation(); onSelect(f); }}
                        sx={{ 
                            flexShrink: 0, 
                            px: 1.25, 
                            fontSize: "0.7rem", 
                            minWidth: "unset" 
                        }}
                    >
                        Tasks
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );
};


const DesktopFlightRow = ({ f, idx, prevTa, td, onSelect, isLast }) => {
    // const preStatus = f.prevFlightStatus;
    const curStatus = f.flightStatus?.toLowerCase() !== "not yet departed" ? "Departed" : f.flightStatus;

    return (
        <>
            <Stack
                direction="row"
                alignItems="center"
                px={2}
                py={1.25}
                spacing={2}
                sx={{
                    "&:hover": { bgcolor: "action.hover" },
                    transition: "background 0.15s ease-in-out",
                }}
            >
                <Chip
                    label={idx + 1}
                    variant="outlined"
                    color="info"
                    size="small"
                    sx={{ fontSize: "0.7rem", height: 20, minWidth: 32, flexShrink: 0 }}
                />

                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: 200, flexShrink: 0 }}>
                    <FlightLandIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>{prevTa || ""}</Typography>
                    {f.prevFlightStatus && (
                        <Chip
                            size="small"
                            variant="outlined"
                            color={getStatusColor(f.prevFlightStatus)}
                            label={f.prevFlightStatus}
                            sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: 200, flexShrink: 0 }}>
                    <FlightTakeoffIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>{td || ""}</Typography>
                    {curStatus && (
                        <Chip
                            size="small"
                            variant="outlined"
                            color={getStatusColor(curStatus)}
                            label={curStatus}
                            sx={{ fontSize: "0.65rem", height: 18 }}
                        />
                    )}
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: 85, flexShrink: 0 }}>
                    <AirlinesIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={500} noWrap>{f.flightDesc}</Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: 110, flexShrink: 0 }}>
                    <FlightIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>{f.dep || "—"} → {f.arr || "—"}</Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ width: 140, flexShrink: 0 }}>
                    <AirplaneTicketIcon fontSize="small" color="action" />
                    <Typography variant="body2" noWrap>{f.acType || "—"} · {f.flightReg || "—"}</Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} flex={1} minWidth={80}>
                    {typeof f.progress === "number" ? (
                        <>
                            <Box flex={1}>
                                <LinearProgress
                                    variant="determinate"
                                    value={f.progress}
                                    color={f.progress === 100 ? "success" : "primary"}
                                    sx={{ height: 6, borderRadius: 1 }}
                                />
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                {f.progress}%
                            </Typography>
                        </>
                    ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                </Stack>

                <Button
                    disabled={f.flightStatus?.toLowerCase() !== "not yet departed"}
                    size="small"
                    variant="outlined"
                    color="primary"
                    startIcon={<AssignmentIcon fontSize="small" />}
                    onClick={(e) => { e.stopPropagation(); onSelect(f); }}
                    sx={{ flexShrink: 0 }}
                >
                    Tasks
                </Button>
            </Stack>
            {!isLast && <Divider />}
        </>
    );
};


const FlightList = ({ flights = [], onSelect }) => {
    const timeMode = useSelector((state) => state.timemode);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    if (!flights.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <CircularProgress />
            </FlexCenter>
        );
    }

    return (
        <Box
            sx={isMobile ? {
                display: "flex",
                flexDirection: "column",
                gap: 1,
            } : {
                width: "100%",
                borderRadius: 2,
                border: `1px solid ${theme.palette.neutral.deep}`,
                bgcolor: "background.paper",
                overflow: "hidden",
            }}
        >
            {flights.map((f, idx) => {
                const prevTa = timeMode === "UTC"
                    ? formatTZDate(f?.prevFullAta || f?.prevFullEta, "UTC", "HH:ss dd/MM")
                    : formatTZDate(f?.prevFullAtaLocalBase || f?.prevFullEtaLocalBase, "UTC", "HH:ss dd/MM");

                const td = timeMode === "UTC"
                    ? formatTZDate(f?.fullAtd || f?.fullEtd, "UTC", "HH:ss dd/MM")
                    : formatTZDate(f?.fullAtdLocalBase || f?.fullEtdLocalBase, "UTC", "HH:ss dd/MM");


                return isMobile ? (
                    <MobileFlightCard
                        key={f._id || idx}
                        f={f}
                        idx={idx}
                        prevTa={prevTa}
                        td={td}
                        onSelect={onSelect}
                    />
                ) : (
                    <DesktopFlightRow
                        key={f._id || idx}
                        f={f}
                        idx={idx}
                        prevTa={prevTa}
                        td={td}
                        onSelect={onSelect}
                        isLast={idx === flights.length - 1}
                    />
                );
            })}
        </Box>
    );
};

export default FlightList;
